import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getPortalSchedule, type ScheduleEvent } from '@lib/api/portal';
import { CalendarSubscribe } from '@/components/scheduling/CalendarSubscribe';
import { LargeTitleHeader, PullToRefresh } from '@/components/common';

// ── Client schedule: a real calendar ───────────────────────────────────────
// Rebuilt for the in-the-loop pass: month grid (dots = crew visits, soft
// shading = overall project window), tap a day to see it in plain words,
// plus a "Coming up" list. Written for people who don't love technology:
// big targets, no jargon, nothing hidden behind gestures.

const MONTH_LONG = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const WEEKDAYS = ['S','M','T','W','T','F','S'];

const parseDateLocal = (d: string) => {
  const [y, m, day] = String(d).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, day);
};
const toKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const fmtTime = (t: string) => {
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
};

// "Today" / "Tomorrow" / "This Friday" / "Friday, Aug 14"
const friendlyDay = (key: string, todayKey: string) => {
  const d = parseDateLocal(key);
  const today = parseDateLocal(todayKey);
  const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  const weekday = d.toLocaleDateString('en-CA', { weekday: 'long' });
  if (diff > 1 && diff < 7) return `This ${weekday}`;
  return d.toLocaleDateString('en-CA', { weekday: 'long', month: 'short', day: 'numeric' });
};

const openMaps = (address: string) =>
  window.open(`https://maps.google.com/?q=${encodeURIComponent(address)}`, '_blank');

export const ClientSchedulePage = () => {
  const [events, setEvents] = useState<ScheduleEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const todayKey = toKey(new Date());
  const [viewYear, setViewYear] = useState(() => new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => new Date().getMonth());
  const [selected, setSelected] = useState(todayKey);

  const loadSchedule = useCallback(async () => {
    try {
      const data = await getPortalSchedule();
      setEvents(data);
      setError(null);
    } catch {
      setError('We couldn\'t load your schedule. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSchedule(); }, [loadSchedule]);

  const visits = useMemo(() => events.filter(e => e.status !== 'span'), [events]);
  const spans = useMemo(() => events.filter(e => e.status === 'span'), [events]);

  // date-key → visits that day (multi-day visits count every covered day)
  const visitsByDay = useMemo(() => {
    const map = new Map<string, ScheduleEvent[]>();
    for (const v of visits) {
      if (!v.scheduled_date) continue;
      const start = parseDateLocal(v.scheduled_date);
      const end = v.end_date ? parseDateLocal(v.end_date) : start;
      for (let d = new Date(start); d <= end && map.size < 500; d.setDate(d.getDate() + 1)) {
        const k = toKey(d);
        if (!map.has(k)) map.set(k, []);
        map.get(k)!.push(v);
      }
    }
    return map;
  }, [visits]);

  const inProjectWindow = useCallback((key: string) => {
    const d = parseDateLocal(key);
    return spans.some(s => {
      if (!s.scheduled_date) return false;
      const start = parseDateLocal(s.scheduled_date);
      const end = s.end_date ? parseDateLocal(s.end_date) : start;
      return d >= start && d <= end;
    });
  }, [spans]);

  const changeMonth = (delta: number) => {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  const goToday = () => {
    const now = new Date();
    setViewYear(now.getFullYear());
    setViewMonth(now.getMonth());
    setSelected(todayKey);
  };

  // Build the month grid (weeks of 7, Sunday first)
  const weeks = useMemo(() => {
    const first = new Date(viewYear, viewMonth, 1);
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const cells: (string | null)[] = Array(first.getDay()).fill(null);
    for (let day = 1; day <= daysInMonth; day++) cells.push(toKey(new Date(viewYear, viewMonth, day)));
    while (cells.length % 7 !== 0) cells.push(null);
    const out: (string | null)[][] = [];
    for (let i = 0; i < cells.length; i += 7) out.push(cells.slice(i, i + 7));
    return out;
  }, [viewYear, viewMonth]);

  const selectedVisits = visitsByDay.get(selected) || [];
  const upcoming = useMemo(() => {
    return visits
      .filter(v => (v.scheduled_date || '').slice(0, 10) >= todayKey)
      .sort((a, b) => String(a.scheduled_date).localeCompare(String(b.scheduled_date)))
      .slice(0, 5);
  }, [visits, todayKey]);

  if (loading) return (
    <div className="space-y-3">
      <div className="h-80 animate-pulse rounded-2xl bg-gray-100" />
      {[...Array(2)].map((_, i) => <div key={i} className="h-20 animate-pulse rounded-xl bg-gray-100" />)}
    </div>
  );
  if (error) return (
    <div className="px-4 py-6 text-base text-red-700 flex items-center gap-2 bg-red-50 rounded-xl border border-red-200">
      <span className="material-symbols-outlined">error</span>{error}
    </div>
  );

  return (
    <PullToRefresh onRefresh={loadSchedule}>
      <div className="space-y-5">
        <LargeTitleHeader
          title="Schedule"
          subtitle="When our crew will be at your project"
          actions={<CalendarSubscribe compact />}
        />

        {/* Month calendar */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <button onClick={() => changeMonth(-1)} aria-label="Previous month"
              className="w-11 h-11 rounded-xl border border-gray-200 flex items-center justify-center text-gray-600 active:bg-gray-100">
              <span className="material-symbols-outlined">chevron_left</span>
            </button>
            <div className="text-center">
              <p className="text-lg font-black text-[#2C1810]">{MONTH_LONG[viewMonth]} {viewYear}</p>
              {toKey(new Date()).slice(0, 7) !== `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}` && (
                <button onClick={goToday} className="text-xs font-bold text-[#C77D3F]">Back to today</button>
              )}
            </div>
            <button onClick={() => changeMonth(1)} aria-label="Next month"
              className="w-11 h-11 rounded-xl border border-gray-200 flex items-center justify-center text-gray-600 active:bg-gray-100">
              <span className="material-symbols-outlined">chevron_right</span>
            </button>
          </div>

          <div className="grid grid-cols-7 mb-1">
            {WEEKDAYS.map((w, i) => (
              <div key={i} className="text-center text-[11px] font-bold text-gray-400 py-1">{w}</div>
            ))}
          </div>
          {weeks.map((week, wi) => (
            <div key={wi} className="grid grid-cols-7">
              {week.map((key, di) => {
                if (!key) return <div key={di} className="h-12" />;
                const dayNum = Number(key.slice(8, 10));
                const hasVisit = visitsByDay.has(key);
                const inWindow = inProjectWindow(key);
                const isToday = key === todayKey;
                const isSelected = key === selected;
                return (
                  <button key={di} onClick={() => setSelected(key)}
                    className={`h-12 flex flex-col items-center justify-center rounded-xl relative
                      ${isSelected ? 'bg-[#C77D3F] text-white' : inWindow ? 'bg-[#C77D3F]/[0.07]' : ''}
                      ${isToday && !isSelected ? 'ring-2 ring-[#C77D3F]' : ''}`}>
                    <span className={`text-sm ${isSelected ? 'font-black' : isToday ? 'font-black text-[#C77D3F]' : 'font-medium text-gray-800'}`}>
                      {dayNum}
                    </span>
                    {hasVisit && (
                      <span className={`w-1.5 h-1.5 rounded-full mt-0.5 ${isSelected ? 'bg-white' : 'bg-[#C77D3F]'}`} />
                    )}
                  </button>
                );
              })}
            </div>
          ))}

          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#C77D3F]" /> Crew visit</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#C77D3F]/[0.12]" /> Project underway</span>
          </div>
        </div>

        {/* Selected day, in plain words */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <p className="text-base font-black text-[#2C1810]">
            {parseDateLocal(selected).toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
          {selectedVisits.length === 0 ? (
            <p className="text-sm text-gray-500 mt-1">
              {inProjectWindow(selected)
                ? 'No crew visit booked this day, but your project is underway.'
                : 'Nothing scheduled this day.'}
            </p>
          ) : selectedVisits.map(v => (
            <div key={v.id} className="mt-3 flex gap-3 items-start">
              <span className="material-symbols-outlined text-[#C77D3F] mt-0.5">construction</span>
              <div className="min-w-0">
                <p className="font-semibold text-gray-900">{v.job_title}</p>
                <p className="text-sm text-gray-600">
                  {v.start_time
                    ? `Crew on site ${fmtTime(v.start_time)}${v.end_time ? ` to ${fmtTime(v.end_time)}` : ''}`
                    : 'Crew on site during the day'}
                </p>
                {v.site_address && (
                  <button type="button" onClick={() => openMaps(v.site_address!)}
                    className="mt-1 flex items-center gap-1 text-sm text-[#C77D3F] font-medium active:opacity-70">
                    <span className="material-symbols-outlined text-base">location_on</span>
                    <span className="truncate">{v.site_address}</span>
                  </button>
                )}
                {/* CR #134 — what's actually planned that day */}
                {(v.subtasks || []).length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs font-bold uppercase tracking-wide text-gray-400">Planned that day</p>
                    <ul className="mt-1 space-y-1">
                      {(v.subtasks || []).map(t => (
                        <li key={t.id} className="flex items-start gap-1.5 text-sm text-gray-700">
                          <span className={`material-symbols-outlined text-base mt-0.5 ${t.status === 'completed' ? 'text-green-600' : 'text-gray-300'}`}>
                            {t.status === 'completed' ? 'check_circle' : 'radio_button_unchecked'}
                          </span>
                          <span className={t.status === 'completed' ? 'line-through text-gray-400' : ''}>{t.title}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {v.job_id && (
                  <Link to={`/portal/client/jobs/${v.job_id}`} className="mt-1 inline-flex items-center gap-1 text-sm font-bold text-[#C77D3F]">
                    See project details <span className="material-symbols-outlined text-base">arrow_forward</span>
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* CR #152 — an active project is NEVER invisible here. Erik's Wilcox
            case: the job was running (window shaded) but its only crew visit
            was in the past, so "Coming up" was empty and the whole tab read as
            "nothing scheduled". This section lists every live project with its
            date window, and says plainly when no crew day is booked yet. */}
        {spans.length > 0 && (
          <div className="space-y-2.5">
            <p className="text-sm font-bold text-gray-500 uppercase tracking-wide">Your projects right now</p>
            {spans.map(s => {
              const startKey = String(s.scheduled_date || '').slice(0, 10);
              const endKey = s.end_date ? String(s.end_date).slice(0, 10) : '';
              const hasFutureVisit = visits.some(v =>
                v.job_id === s.job_id && String(v.scheduled_date || '').slice(0, 10) >= todayKey);
              const started = startKey && startKey <= todayKey;
              const fmt = (k: string) =>
                parseDateLocal(k).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
              return (
                <div key={s.job_id} className="bg-white rounded-2xl border border-gray-200 p-4">
                  <div className="flex items-start gap-3">
                    <span className="material-symbols-outlined text-[#C77D3F] mt-0.5">construction</span>
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-gray-900">{s.job_title}</p>
                      <p className="text-sm text-gray-600 mt-0.5">
                        {started ? `Under way — started ${fmt(startKey)}` : `Starts ${fmt(startKey)}`}
                        {endKey ? `, planned finish ${fmt(endKey)}` : ''}.
                      </p>
                      {!hasFutureVisit && (
                        <p className="text-sm text-gray-500 mt-1.5 bg-[#FAF6F0] rounded-lg px-3 py-2">
                          No crew day booked on the calendar yet — we book the days as we schedule
                          the work, and each one will show up here with a dot.
                        </p>
                      )}
                      <Link to={`/portal/client/jobs/${s.job_id}`}
                        className="mt-1.5 inline-flex items-center gap-1 text-sm font-bold text-[#C77D3F]">
                        See project details <span className="material-symbols-outlined text-base">arrow_forward</span>
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Coming up */}
        {upcoming.length > 0 && (
          <div className="space-y-2.5">
            <p className="text-sm font-bold text-gray-500 uppercase tracking-wide">Coming up</p>
            {upcoming.map(e => (
              <button key={e.id} type="button"
                onClick={() => {
                  const k = String(e.scheduled_date).slice(0, 10);
                  const d = parseDateLocal(k);
                  setViewYear(d.getFullYear()); setViewMonth(d.getMonth()); setSelected(k);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="w-full text-left bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3 active:bg-[#FAF6F0]">
                <span className="material-symbols-outlined text-[#C77D3F]">event</span>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-900">{friendlyDay(String(e.scheduled_date).slice(0, 10), todayKey)}</p>
                  <p className="text-sm text-gray-600 truncate">
                    {e.job_title}
                    {e.start_time ? ` · ${fmtTime(e.start_time)}` : ''}
                  </p>
                </div>
                <span className="material-symbols-outlined text-gray-300">chevron_right</span>
              </button>
            ))}
          </div>
        )}

        {visits.length === 0 && (
          <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
            <span className="material-symbols-outlined text-gray-300 text-5xl">calendar_month</span>
            <p className="text-base font-semibold text-gray-600 mt-3">No visits booked yet</p>
            <p className="text-sm text-gray-400 mt-1">When we schedule your crew, the days will show up here with a dot.</p>
          </div>
        )}
      </div>
    </PullToRefresh>
  );
};
